import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { VscNewFile } from 'react-icons/vsc';
import { useDispatch, useSelector } from 'react-redux';
import AccesRestreins from '../../../hoc/AccesRestreins/AccesRestreins';
import { checkMot } from '../../../ressources/functionJS/checkMot';
import * as privilege from '../../../ressources/glossaires/glossaire_privileges';
import * as requestType from '../../../ressources/glossaires/glossaire_requetes';
import * as type_btn from '../../../ressources/glossaires/glossaire_type_de_bouton';
import * as actions from '../../../services/actions';
import Bouton from '../../UI/Bouton/Bouton';
import DataGrid from '../../UI/DataGrid/DataGrid';
import Toolbar from '../../UI/Toolbar/Toolbar';
import styles from './TableauVue.module.scss';

// Column definitions factory
const createColumn = (field, labelKey, width, flex) => ({
  field,
  headerName: checkMot(labelKey),
  width,
  sortable: true,
  align: 'center',
  headerAlign: 'center',
  ...(flex && { flex }),
});

// Column configuration [field, labelKey, width, flex]
const COLUMN_CONFIG = [
  ['view_id', 'id', 100, null],
  ['project_id', 'id_projet', 120, null],
  ['view_name', 'nom', 200, 1],
  ['view_title', 'descriptif', 250, 1],
];

const TableauVue = ({ creer, projetSelectione }) => {
  const dispatch = useDispatch();

  // Redux state
  const interfaceAdmin = useSelector(
    (state) => state.authentifie.interfaceAdmin
  );
  const listeVues = useSelector((state) => state.vue.views_list);
  const filtre_projects_list_id = useSelector(
    (state) => state.authentifie.filtre_projects_list_id
  );
  const project_id_url = useSelector(
    (state) => state.interfaceClient.project_id_url
  );

  // Local state
  const [selectionModel, setSelectionModel] = useState([]);
  const [searchValue, setSearchValue] = useState('');

  // Memoized columns
  const columns = useMemo(() => {
    return COLUMN_CONFIG.map(([field, labelKey, width, flex]) => {
      const column = createColumn(field, labelKey, width, flex);

      // Hide ID and project columns if not admin
      if (!interfaceAdmin && (field === 'view_id' || field === 'project_id')) {
        return { ...column, display: 'none' };
      }

      return column;
    });
  }, [interfaceAdmin]);

  // Effect for initial data fetch
  useEffect(() => {
    const projectIds =
      !isNaN(project_id_url) && project_id_url !== ''
        ? [parseInt(project_id_url)]
        : filtre_projects_list_id;

    if (projectIds.length > 0) {
      dispatch(actions.reqRecupererListeVues(projectIds));
    }
  }, [project_id_url, filtre_projects_list_id, dispatch]);

  // Handle row selection
  const handleSelectionModelChange = useCallback(
    (newSelection) => {
      setSelectionModel(newSelection);
      const selectedRow = listeVues?.find(
        (row) => newSelection[0] === row.view_id
      );

      if (selectedRow) {
        projetSelectione(selectedRow);
      }
    },
    [listeVues, projetSelectione]
  );

  // Refresh handler
  const handleRefresh = useCallback(() => {
    const projectIds =
      !isNaN(project_id_url) && project_id_url !== ''
        ? [parseInt(project_id_url)]
        : filtre_projects_list_id;

    if (projectIds.length > 0) {
      dispatch(actions.reqRecupererListeVues(projectIds));
    }
  }, [dispatch, project_id_url, filtre_projects_list_id]);

  // Search handlers
  const handleSearchChange = useCallback((e) => {
    setSearchValue(e.target.value);
  }, []);

  const handleSearchClear = useCallback(() => {
    setSearchValue('');
  }, []);

  // Filtered rows with memoization
  const filteredRows = useMemo(() => {
    if (!listeVues) return [];

    return listeVues.filter((vue) => {
      // Filter by project
      const projectMatch = interfaceAdmin
        ? filtre_projects_list_id.includes(parseInt(vue.project_id))
        : parseInt(vue.project_id) === parseInt(project_id_url);

      if (!projectMatch) return false;
      if (!searchValue) return true;

      // Filter by search value
      const searchLower = searchValue.toLowerCase();
      return Object.values(vue).some((value) =>
        String(value).toLowerCase().includes(searchLower)
      );
    });
  }, [
    listeVues,
    interfaceAdmin,
    filtre_projects_list_id,
    project_id_url,
    searchValue,
  ]);

  return (
    <div className={styles.container}>
      {/* Toolbar */}
      <Toolbar
        searchValue={searchValue}
        onSearchChange={handleSearchChange}
        onSearchClear={handleSearchClear}
        searchPlaceholder="recherche"
        onRefresh={handleRefresh}
        refreshRequestType={requestType.recuperation_de_la_liste_des_vues}
        refreshMessage={checkMot('maj_vues_en_cour')}
      >
        {/* Create Button with access restrictions */}
        <AccesRestreins listNumPrivilege={[privilege.creer_des_vues]}>
          <Bouton
            hoverText={checkMot('creer')}
            type={type_btn.outil}
            rond
            cliquer={creer}
          >
            <VscNewFile size={15} />
          </Bouton>
        </AccesRestreins>
      </Toolbar>

      {/* DataGrid */}
      <DataGrid
        rows={filteredRows}
        columns={columns}
        getRowId={(row) => row.view_id}
        onRowSelectionModelChange={handleSelectionModelChange}
        rowSelectionModel={selectionModel}
        pageSizeOptions={[25, 50, 100]}
        initialPageSize={25}
        autoRowHeight
      />
    </div>
  );
};

export default TableauVue;
